// Achievements Gallery Component for Memara
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  useActivityTimeline,
  type ActivityFeedScope,
} from '../hooks/useActivityTimeline';
import { Achievement } from '../services/activityTimeline';
import { Trophy, Star, Crown, Diamond, Medal, CheckCircle, Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AchievementsGalleryProps {
  showHeader?: boolean;
  compact?: boolean;
  className?: string;
  categoryFilter?: Achievement['category'] | 'all';
  activityFeedScope?: ActivityFeedScope;
}

const AchievementsGallery: React.FC<AchievementsGalleryProps> = ({ 
  showHeader = true, 
  compact = false,
  className = '',
  categoryFilter = 'all',
  activityFeedScope = 'contextSpace',
}) => {
  const { achievements, unlockedAchievements, achievementsByCategory, isLoading } =
    useActivityTimeline({ activityFeedScope });
  const [selectedCategory, setSelectedCategory] = useState<Achievement['category'] | 'all'>(categoryFilter);
  const [showOnlyUnlocked, setShowOnlyUnlocked] = useState(false);
  const { t } = useTranslation();

  const getRarityColor = (rarity: Achievement['rarity']) => {
    switch (rarity) {
      case 'common':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'uncommon':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rare':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'epic':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'legendary':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getTierIcon = (tier: Achievement['tier']) => {
    switch (tier) {
      case 'bronze':
        return <Medal className="w-4 h-4 text-amber-600" />;
      case 'silver':
        return <Medal className="w-4 h-4 text-gray-500" />;
      case 'gold':
        return <Trophy className="w-4 h-4 text-yellow-500" />;
      case 'platinum':
        return <Crown className="w-4 h-4 text-blue-500" />;
      case 'diamond':
        return <Diamond className="w-4 h-4 text-purple-500" />;
      default:
        return <Star className="w-4 h-4 text-gray-400" />;
    }
  };

  const getCategoryIcon = (category: Achievement['category']) => {
    switch (category) {
      case 'creation':
        return '🧠';
      case 'search':
        return '🔍';
      case 'streak':
        return '🔥';
      case 'milestone':
        return '🎯';
      case 'mastery':
        return '⚡';
      default:
        return '🏆';
    }
  };

  const getCategoryTranslationKey = (category: Achievement['category']) => {
    switch (category) {
      case 'creation':
        return t('achievements.categories.creation', { ns: 'dashboard' }) as string;
      case 'search':
        return t('achievements.categories.search', { ns: 'dashboard' }) as string;
      case 'streak':
        return t('achievements.categories.streak', { ns: 'dashboard' }) as string;
      case 'milestone':
        return t('achievements.categories.milestone', { ns: 'dashboard' }) as string;
      case 'mastery':
        return t('achievements.categories.mastery', { ns: 'dashboard' }) as string;
      default:
        return t('achievements.categories.all', { ns: 'dashboard' }) as string;
    }
  };

  const categories: (Achievement['category'] | 'all')[] = ['all', 'creation', 'search', 'streak', 'milestone', 'mastery'];

  const filteredAchievements = selectedCategory === 'all' 
    ? achievements 
    : achievementsByCategory(selectedCategory);

  const displayAchievements = showOnlyUnlocked 
    ? filteredAchievements.filter(a => a.isUnlocked)
    : filteredAchievements;

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {showHeader && (
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('achievements.title', { ns: 'dashboard' }) as string}</h3>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-32 bg-gray-200 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('achievements.title', { ns: 'dashboard' }) as string}</h3>
            <span className="text-sm text-gray-500">
              {t('achievements.unlockedCount', { unlocked: unlockedAchievements.length, total: achievements.length, ns: 'dashboard' }) as string}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowOnlyUnlocked(!showOnlyUnlocked)}
              className={`
                flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors
                ${showOnlyUnlocked 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
              `}
            >
              {showOnlyUnlocked ? <CheckCircle className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
              {t('achievements.unlockedOnly', { ns: 'dashboard' }) as string}
            </button>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`
              flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors
              ${selectedCategory === category
                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
            `}
          >
            {category !== 'all' && (
              <span className="text-sm">{getCategoryIcon(category)}</span>
            )}
            {category === 'all' ? t('achievements.categories.all', { ns: 'dashboard' }) as string : getCategoryTranslationKey(category)}
          </button>
        ))}
      </div>

      {/* Achievements Grid */}
      <div className={`grid gap-4 ${compact ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {displayAchievements.map((achievement, index) => (
          <motion.div
            key={achievement.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.3, 
              delay: index * 0.05,
              ease: 'easeOut'
            }}
            className={`
              relative p-4 rounded-lg border transition-all duration-200 hover:shadow-lg
              ${achievement.isUnlocked 
                ? 'bg-white border-gray-200 shadow-sm' 
                : 'bg-gray-50 border-gray-100 opacity-75'
              }
              ${compact ? 'p-3' : 'p-4'}
            `}
          >
            {/* Achievement Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`text-2xl ${achievement.isUnlocked ? '' : 'grayscale opacity-50'}`}>
                  {achievement.icon}
                </div>
                <div className="flex items-center gap-1">
                  {getTierIcon(achievement.tier)}
                  {achievement.isUnlocked && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                </div>
              </div>
              
              <div className={`
                px-2 py-1 rounded-full text-xs font-medium border
                ${getRarityColor(achievement.rarity)}
              `}>
                {achievement.rarity}
              </div>
            </div>

            {/* Achievement Info */}
            <div className="space-y-2">
              <h4 className={`font-semibold ${compact ? 'text-sm' : 'text-base'} ${
                achievement.isUnlocked ? 'text-gray-900' : 'text-gray-600'
              }`}>
                {achievement.title}
              </h4>
              
              {!compact && (
                <p className={`text-sm ${
                  achievement.isUnlocked ? 'text-gray-600' : 'text-gray-500'
                }`}>
                  {achievement.description}
                </p>
              )}

              {/* Progress Bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{t('achievements.progress', { ns: 'dashboard' }) as string}</span>
                  <span>{Math.round(achievement.progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${achievement.progress}%` }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    className={`h-2 rounded-full ${
                      achievement.isUnlocked 
                        ? 'bg-green-500' 
                        : achievement.progress > 0 
                          ? 'bg-blue-500' 
                          : 'bg-gray-300'
                    }`}
                  />
                </div>
              </div>

              {/* Requirement */}
              <div className="text-xs text-gray-500">
                <span className="font-medium">{t('achievements.goal', { ns: 'dashboard' }) as string}:</span> {achievement.requirement.value} {
                  achievement.requirement.type.replace('_', ' ')
                }
              </div>

              {/* Unlock Date */}
              {achievement.isUnlocked && achievement.unlockedAt && (
                <div className="text-xs text-green-600 font-medium">
                  ✓ {t('achievements.unlockedOn', { ns: 'dashboard', date: achievement.unlockedAt.toLocaleDateString() }) as string}
                </div>
              )}
            </div>

            {/* Unlock Animation */}
            {achievement.isUnlocked && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ duration: 0.5 }}
                className="absolute -top-2 -right-2"
              >
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {displayAchievements.length === 0 && (
        <div className="text-center py-8">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-sm text-gray-500">{t('achievements.noAchievementsInCategory', { ns: 'dashboard' }) as string}</p>
          <p className="text-xs text-gray-400 mt-1">
            {showOnlyUnlocked ? (t('achievements.tryUnlockingFirst', { ns: 'dashboard' }) as string) : (t('achievements.startUsingMemara', { ns: 'dashboard' }) as string)}
          </p>
        </div>
      )}

      {/* Quick Stats */}
      {!compact && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{unlockedAchievements.length}</div>
            <div className="text-xs text-gray-600">{t('achievements.stats.unlocked', { ns: 'dashboard' }) as string}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {achievements.filter(a => a.progress > 0 && !a.isUnlocked).length}
            </div>
            <div className="text-xs text-gray-600">{t('achievements.stats.inProgress', { ns: 'dashboard' }) as string}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {achievements.filter(a => a.rarity === 'rare' || a.rarity === 'epic' || a.rarity === 'legendary').filter(a => a.isUnlocked).length}
            </div>
            <div className="text-xs text-gray-600">{t('achievements.stats.rarePlus', { ns: 'dashboard' }) as string}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {Math.round((unlockedAchievements.length / achievements.length) * 100)}%
            </div>
            <div className="text-xs text-gray-600">{t('achievements.stats.complete', { ns: 'dashboard' }) as string}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementsGallery; 